/**
 * @file index.tsx
 *
 * @fileoverview Renders a featured section with offset an icon grid.
 */

import React, { useMemo } from 'react';
import { FeatureGridWithOffsetIconsParagraph } from '~/jobiqo/types/types';
import dynamic from 'next/dynamic';
import { MenuIcon } from '~/vcl/icons/Menu';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { Badge } from '~/vcl/atoms/03-badge';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface FeatureGridWithOffsetIconsProps {
  row: FeatureGridWithOffsetIconsParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}
export const FeatureGridWithOffsetIcons = ({
  row,
  maxContainerSize,
}: FeatureGridWithOffsetIconsProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const LandingPageIcon = useMemo(
    () =>
      dynamic(() => import('~/jobiqo/components/Landingpage/LandingPageIcon'), {
        ssr: false,
        loading: () => (
          <MenuIcon
            color={frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.ICON_COLOR}
            height={frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.ICON_SIZE}
            width={frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.ICON_SIZE}
          />
        ),
      }),
    [frontendTheme],
  );

  const ParagraphInfo = ({ children }) =>
    frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.INTRO_BADGE ? (
      <Badge>{children}</Badge>
    ) : (
      <Text
        marginTop="0"
        textAlign={frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.TEXT_ALIGN}
        color={row.color as any}
        fontWeight="bold"
        textTransform="uppercase"
      >
        {children}
      </Text>
    );

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={row.spacing ?? 12}
      paddingBottom={row.spacing ?? 12}
      backgroundColorOverride={
        row.background ? `var(--color-${row.background})` : `var(--color-white)`
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        {row.featuredOffsetSubTitle && (
          <>
            <ParagraphInfo>{row.featuredOffsetSubTitle}</ParagraphInfo>
            <Spacing size="medium" />
          </>
        )}
        {row.featuredOffsetTitle && (
          <>
            <Text
              textAlign={
                frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.TEXT_ALIGN
              }
              tag="h2"
              fontSize={[5, 7]}
              lineHeight={[5, 7]}
            >
              {row.featuredOffsetTitle}
            </Text>
            <Spacing size="large" />
          </>
        )}
        {row.text && (
          <>
            <Text
              textAlign={
                frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.TEXT_ALIGN
              }
              color="gray6"
              fontSize={4}
              lineHeight={5}
            >
              {row.text}
            </Text>
            <Spacing marginBottom={11} />
          </>
        )}

        <Grid columns={[1, 3]} gap={7}>
          {row.items?.map((item, index) => {
            return (
              item && (
                <Cell display="grid" key={index}>
                  <Box
                    backgroundColor={
                      frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.BOX_COLOR
                    }
                  >
                    <FlexItems justifyContent="center">
                      {(item.icon || item.iconCustom) && (
                        <FlexItem marginTop={'-50px'} marginBottom={6}>
                          <Box
                            marginTop={0}
                            paddingTop={6}
                            paddingBottom={5}
                            paddingLeft={6}
                            paddingRight={6}
                            backgroundColorOverride={
                              frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                                .ICON_BOX_COLOR
                                ? `var(--color-${frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS.ICON_BOX_COLOR})`
                                : `var(--color-${row.color})`
                            }
                            {...(frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                              .ICON_BOX_SHADOW
                              ? {
                                  boxShadow:
                                    frontendTheme.LANDING_PAGES
                                      .FEATURE_GRID_ICONS.ICON_BOX_SHADOW,
                                }
                              : null)}
                            {...(frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                              .ICON_BOX_RADIUS
                              ? {
                                  borderRadius:
                                    frontendTheme.LANDING_PAGES
                                      .FEATURE_GRID_ICONS.ICON_BOX_RADIUS,
                                }
                              : null)}
                          >
                            <LandingPageIcon
                              icon={item?.icon}
                              iconCustom={item?.iconCustom}
                              color={
                                frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                                  .ICON_COLOR ?? 'white'
                              }
                              height={
                                frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                                  .ICON_SIZE
                              }
                              width={
                                frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                                  .ICON_SIZE
                              }
                            />
                          </Box>
                        </FlexItem>
                      )}
                    </FlexItems>
                    <Text
                      textAlign="center"
                      tag="p"
                      fontSize={4}
                      {...(frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                        .BOX_TITLE_COLOR
                        ? {
                            color:
                              frontendTheme.LANDING_PAGES.FEATURE_GRID_ICONS
                                .BOX_TITLE_COLOR,
                          }
                        : null)}
                    >
                      {item.title}
                    </Text>
                    <Text textAlign="center" color="gray4">
                      {item.text}
                    </Text>
                    <Spacing size="medium" />
                  </Box>
                </Cell>
              )
            );
          })}
        </Grid>
      </Main>
    </Box>
  );
};
export default FeatureGridWithOffsetIcons;
